import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import { AreaChartComponent_v2 as AreaChartComponent } from '../../AreaChartComponent_v2.js';
import useWidgetData from '../hooks/useWidgetData';
import useFilterStore, { useFilterStoreWithMomentDates } from '../../../store/filterStore.js';

// Default grouping field to use if the configured one is invalid
const DEFAULT_GROUPING = 'cost_resource_type';

/**
 * CostTrendChartWidget - Shows cost trend data as a stacked area chart
 * Based on AreaChartComponent_v2 from AnalyzerView
 * Supports configurable grouping via widget config sidebar
 */
const CostTrendChartWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false,
  onFilterAdd
}) => {
  // Get global date range and filters from filter store (same as AnalyzerView)
  const { startDate, endDate } = useFilterStoreWithMomentDates();
  const globalFilters = useFilterStore((state) => state.filters);

  // Use dateGrouping from config (set via sidebar), with local state for chart button changes
  const configDateGrouping = useMemo(() => config.dateGrouping || { label: "Day", value: "MMM D" }, [config.dateGrouping]);
  const [localDateGrouping, setLocalDateGrouping] = useState(configDateGrouping);
  
  // Sync local state with config when config changes
  const dateGrouping = useMemo(() => {
    // Config changes take precedence
    if (config.dateGrouping?.label !== localDateGrouping?.label) {
      return configDateGrouping;
    }
    return localDateGrouping;
  }, [config.dateGrouping, localDateGrouping, configDateGrouping]);

  // Convert global filters to API format (same as AnalyzerView's buildAnalyzerRequest)
  const apiFilters = useMemo(() => {
    if (!Array.isArray(globalFilters)) return [];
    
    return globalFilters.map((filter) => {
      let field = filter.category;
      
      // Translate organisation level filter to organisation_name field (like AnalyzerView)
      if (field.slice(0, 14) === "Organisation l") {
        field = "organisation_name";
      }
      
      return {
        field: field,
        operator: "=",
        values: filter.selectedValues,
        is_tag: filter.isTag,
      };
    });
  }, [globalFilters]);

  // Validate and sanitize the grouping value before making API calls
  // Also include date_granularity and dates to match AnalyzerView's request format
  const validatedConfig = useMemo(() => {
    const currentGrouping = config?.grouping || config?.dataSource?.body?.grouping?.[0];
    
    // Check if the grouping is valid - must be a non-empty string
    // Allow alphanumeric, underscores, spaces (for groupings like "cost resource type", "Organisation l1")
    const isValidGrouping = currentGrouping && 
      typeof currentGrouping === 'string' && 
      currentGrouping.trim() !== '' &&
      // Allow letters, numbers, underscores, and spaces - common patterns in grouping field names
      /^[a-z][a-z0-9_ ]*$/i.test(currentGrouping);
    
    const safeGrouping = isValidGrouping ? currentGrouping : DEFAULT_GROUPING;
    
    // Return a config with validated grouping in the dataSource body
    if (!config?.dataSource?.body) {
      return config;
    }

    // Build filters array - include level filter for Organisation groupings (like AnalyzerView)
    const reqFilters = [...apiFilters];
    if (safeGrouping.slice(0, 14) === "Organisation l") {
      const level = safeGrouping.split(" ")[1]?.substring(1);
      if (level) {
        reqFilters.push({ field: "level", operator: "=", values: [level] });
      }
    }
    
    return {
      ...config,
      grouping: safeGrouping,
      dataSource: {
        ...config.dataSource,
        body: {
          ...config.dataSource.body,
          grouping: [safeGrouping],
          // Include filters from global filter store (like AnalyzerView)
          filters: reqFilters,
          // Include date_granularity to match AnalyzerView's request format
          date_granularity: dateGrouping.label,
          // Use global date range from filter store (same as AnalyzerView)
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: endDate.format("YYYY-MM-DD"),
          // Disable grouped_results - not needed and makes response much larger
          add_grouped_results: false
        }
      }
    };
  }, [config, dateGrouping.label, startDate, endDate, apiFilters]);

  // Pass empty filters to useWidgetData since we're including them in the body
  const { data, isLoading, error } = useWidgetData(validatedConfig, []);

  // Transform API response data to match AreaChartComponent expected format
  const transformedData = useMemo(() => {
    if (!data) return [];

    // Handle CloudCost.details format (from /get-cloud-cost endpoint)
    // The API returns: { CloudCost: { details: [...], grouped_totals: [...] } }
    const details = data?.CloudCost?.details || data?.details || data?.data || [];
    
    if (!Array.isArray(details) || details.length === 0) {
      return [];
    }

    // Get the grouping field from validated config
    const groupingField = validatedConfig.grouping || DEFAULT_GROUPING;

    // Transform to format expected by AreaChartComponent_v2: { time, grouping, measure }
    return details.map((d) => {
      // Handle the API response format with groupings and measures arrays
      // Matches AnalyzerView: d.time = d.groupings[0].value, d.grouping = d.groupings[1].value, d.measure = d.measures[0].value
      let time, grouping, measure;

      if (d.groupings && Array.isArray(d.groupings)) {
        // Format with groupings array (from /get-cloud-cost endpoint)
        time = d.groupings[0]?.value || d.time;
        grouping = d.groupings[1]?.value || d[groupingField] || d.grouping || 'Total';
        measure = d.measures?.[0]?.value ?? d.cost ?? d.measure ?? 0;
      } else {
        // Already transformed or different format
        time = d.time || d.date;
        grouping = d[groupingField] || d.grouping || d.cost_resource_type || 
                   d.service || d.project_name || d.organisation_name || 'Total';
        measure = d.cost ?? d.measure ?? d.value ?? 0;
      }

      return {
        time,
        grouping,
        measure
      };
    });
  }, [data, validatedConfig.grouping]);

  // Handler for date grouping changes (from chart buttons)
  const handleDateGroupingChange = useCallback((newGrouping) => {
    if (newGrouping && typeof newGrouping === 'object') {
      setLocalDateGrouping(newGrouping);
    }
  }, []);

  // Handle filter add events from chart interactions
  const handleFilterAdd = useCallback((filter) => {
    if (onFilterAdd) {
      onFilterAdd(filter);
    }
  }, [onFilterAdd]);

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      <div 
        className="h-full flex flex-col"
        style={{ minHeight: '200px', height: '100%' }}
      >
        {transformedData.length > 0 && (
          <div className="flex-1 flex flex-col h-full">
            <AreaChartComponent
              data={transformedData}
              title={config.title}
              dateFormat={dateGrouping.value}
              legendPosition={config.legendPosition || "bottom"}
              dateGrouping={dateGrouping}
              setDateGrouping={handleDateGroupingChange}
              showButtons={config.showButtons !== false}
              stacked={config.stacked !== false}
              filters={filters}
              setFilters={() => {}} // Handled by parent
              addFilter={handleFilterAdd}
              token={null} // Handled by the hook
              currentGrouping={{ label: validatedConfig.grouping, value: validatedConfig.grouping }}
              height="100%"
              useOwnBackground={false}
              maxGroupings={config.maxGroupings || 10}
              disableSelection={isEditMode}
              showLegend={config.showLegend !== false}
            />
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

CostTrendChartWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    grouping: PropTypes.string,
    dateFormat: PropTypes.string,
    dateGrouping: PropTypes.object,
    showButtons: PropTypes.bool,
    stacked: PropTypes.bool,
    legendPosition: PropTypes.oneOf(['bottom', 'right']),
    showLegend: PropTypes.bool,
    maxGroupings: PropTypes.number,
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool,
  onFilterAdd: PropTypes.func
};

export default CostTrendChartWidget;

